import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

const RAW_BASE_URL =
  process.env['NX_PUBLIC_API_BASE_URL'] || 'http://localhost:8080';
const BASE_URL = RAW_BASE_URL.endsWith('/')
  ? RAW_BASE_URL.slice(0, -1)
  : RAW_BASE_URL;

export interface AccountUser {
  id: string;
  type: string;
  attributes: {
    email: string;
    display_name: string | null;
    role: string;
    created_at: string;
    updated_at: string;
  };
}

export interface Invitation {
  id: string;
  type: string;
  attributes: {
    email: string;
    role: string;
    status: string;
    created_at: string;
    updated_at: string;
  };
}

// GET /accounts/{id}/users
export const useAccountUsers = (accountId: string | undefined) => {
  return useQuery<{ data: AccountUser[] }>({
    queryKey: ['accountUsers', accountId],
    queryFn: async () => {
      const response = await axios.get(
        `${BASE_URL}/accounts/${accountId}/users`,
        { withCredentials: true },
      );
      return response.data;
    },
    enabled: Boolean(accountId),
  });
};

// GET /accounts/{id}/invitations
export const useAccountInvitations = (accountId: string | undefined) => {
  return useQuery<{ data: Invitation[] }>({
    queryKey: ['accountInvitations', accountId],
    queryFn: async () => {
      const response = await axios.get(
        `${BASE_URL}/accounts/${accountId}/invitations`,
        { withCredentials: true },
      );
      return response.data;
    },
    enabled: Boolean(accountId),
  });
};

// POST /accounts/{id}/invitations
export const useInviteUser = (accountId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { email: string; role: string }) => {
      const response = await axios.post(
        `${BASE_URL}/accounts/${accountId}/invitations`,
        {
          data: {
            type: 'invitation',
            attributes: params,
          },
        },
        { withCredentials: true },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['accountInvitations', accountId],
      });
    },
  });
};

// PATCH /accounts/{id}/users/{userId}
export const useUpdateUserRole = (accountId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { userId: string; role: string }) => {
      const response = await axios.patch(
        `${BASE_URL}/accounts/${accountId}/users/${params.userId}`,
        {
          data: {
            type: 'user-role',
            attributes: { role: params.role },
          },
        },
        { withCredentials: true },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['accountUsers', accountId],
      });
    },
  });
};

// DELETE /accounts/{id}/users/{userId}
export const useRemoveUser = (accountId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      await axios.delete(`${BASE_URL}/accounts/${accountId}/users/${userId}`, {
        withCredentials: true,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['accountUsers', accountId],
      });
    },
  });
};

// DELETE /accounts/{id}/invitations/{tokenId}
export const useRevokeInvitation = (accountId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tokenId: string) => {
      await axios.delete(
        `${BASE_URL}/accounts/${accountId}/invitations/${tokenId}`,
        { withCredentials: true },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['accountInvitations', accountId],
      });
    },
  });
};
